import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { PageTitle } from "@/components/layout/PageTitle";
import { CatalogScopeFilters } from "@/components/common/CatalogScopeFilters";
import { useAuth } from "@/hooks/useAuth";
import { useCatalogScope } from "@/hooks/useCatalogScope";
import { useMenuItemsViewMode } from "@/hooks/ui-preferences";
import { MenuItemsTable, type MenuItemsTableParams } from "./MenuItemsTable";
import { MenuItemFormDialog } from "./MenuItemFormDialog";
import { menuItemsOriginState } from "./menu-items-origin";
import { MenuCategoryBar } from "./MenuCategoryBar";

/**
 * The picked organization and concept live in the URL with the rest of the
 * filters, so coming Back from a menu item restores the list as it was left.
 */
export type MenuItemsListingSearchParams = MenuItemsTableParams & {
  organizationId?: number;
  companyId?: number;
};

interface MenuItemsListingProps {
  searchParams: MenuItemsListingSearchParams;
  onNavigate: (
    updater: (prev: MenuItemsListingSearchParams) => MenuItemsListingSearchParams,
  ) => void;
}

/** The Menu Items module's list screen: menu items, an Add button, and the form. */
export function MenuItemsListing({ searchParams, onNavigate }: MenuItemsListingProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const scope = useCatalogScope({
    defaultToFirstConcept: true,
    initialOrganizationId: searchParams.organizationId,
    initialCompanyId: searchParams.companyId,
  });
  const canWrite = hasPermission("menu:write");
  const { viewMode, setViewMode } = useMenuItemsViewMode(scope.isAuthenticated);

  // Mirror the picked concept into the URL. A change of concept starts the
  // list again from page 1.
  const { selectedCompanyId, effectiveOrganizationId, isInternalRecipeViewer } = scope;
  const navigateRef = useRef(onNavigate);
  useEffect(() => {
    navigateRef.current = onNavigate;
  });
  useEffect(() => {
    if (selectedCompanyId == null || selectedCompanyId === searchParams.companyId) return;
    navigateRef.current((prev) => ({
      ...prev,
      companyId: selectedCompanyId,
      organizationId: isInternalRecipeViewer ? (effectiveOrganizationId ?? undefined) : undefined,
      // Categories belong to a concept, so the one picked for the last concept
      // means nothing in this one.
      categoryId: undefined,
      page: 1,
    }));
  }, [selectedCompanyId, effectiveOrganizationId, isInternalRecipeViewer, searchParams.companyId]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  const openEdit = useCallback((id: number) => {
    setEditingId(id);
    setFormOpen(true);
  }, []);

  const openDetail = useCallback(
    (id: number) =>
      void navigate({
        to: "/menus/$menuItemId",
        params: { menuItemId: String(id) },
        // Lets the detail page's Back step back to this list as it is now.
        state: menuItemsOriginState(),
      }),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-5 py-5 px-3">
      <PageTitle
        size="lg"
        title={t`Menu Items`}
        description={t`Everything you sell, with its price and food cost.`}
        className="px-3"
        actions={
          canWrite ? (
            <Button className="h-10 gap-2 rounded-lg" onClick={openCreate}>
              <Plus className="size-4" />
              {t`Add menu item`}
            </Button>
          ) : undefined
        }
      />

      <CatalogScopeFilters scope={scope} />

      {scope.isInternalRecipeViewer && !scope.isInternalScopeReady && (
        <div className="mb-3 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-2 text-sm text-(--color-text-secondary)">
          {t`Select organization and concept to load menu items.`}
        </div>
      )}

      {!scope.hasNoConcepts && scope.isInternalScopeReady && scope.resolvedFilterCompanyId != null && (
        <MenuCategoryBar
          companyId={scope.resolvedFilterCompanyId}
          activeId={searchParams.categoryId}
          onChange={(categoryId) =>
            onNavigate((prev) => ({ ...prev, categoryId, page: 1 }))
          }
          canWrite={canWrite}
        />
      )}

      {scope.hasNoConcepts ? (
        <div className="rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 py-8 text-center text-sm text-(--color-text-secondary)">
          {t`There are no concepts to show menu items for.`}
        </div>
      ) : (
        <MenuItemsTable
          searchParams={searchParams}
          onNavigate={onNavigate}
          companyId={scope.resolvedFilterCompanyId}
          enabled={scope.isAuthenticated && scope.isInternalScopeReady}
          onOpen={openDetail}
          onEdit={openEdit}
          canWrite={canWrite}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          emptyMessage={
            scope.isInternalRecipeViewer && !scope.isInternalScopeReady
              ? t`Select organization and concept to view menu items`
              : t`No menu items found`
          }
        />
      )}

      <MenuItemFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingId(null);
        }}
        editingId={editingId}
        // A new item starts in the category the list is filtered to.
        defaultCategoryId={searchParams.categoryId}
        companyId={scope.dialogFixedCompanyId}
        defaultCompanyId={scope.dialogDefaultCompanyId}
        organizationId={scope.effectiveOrganizationId}
        canSelectOrganization={scope.isInternalRecipeViewer && !scope.fixedOrganizationId}
        // A menu item can only serve a plated recipe that already exists, so
        // send the user to where those are made; Add there offers plated.
        onCreatePlatedRecipe={() => {
          setFormOpen(false);
          void navigate({
            to: "/recipes",
            search: {
              tab: "prepRecipes",
              page: 1,
              pageSize: 50,
              status: "all",
              recipeType: "PLATED",
            },
          });
        }}
      />
    </div>
  );
}
